import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import uniqid from "uniqid";

import apiRoot from "../../../api/apiRoot";
import { FaoBuilderContext } from "context/FaoBuilderContext";
import { ClientContext } from "context/ClientContext";

import {
  groupFundsByName,
  parseCurrency,
  transformInstitutionalFundsData,
  transformSpecialFundAdjustments,
  transformOutsideAidData,
  createSpecialFundAdjustmentsPayload,
} from "../helpers/awarding-strategy.helpers";
import { DEFAULT_OUTSIDE_AID_STATE } from "../constants/awarding-strategy.constants";

export const useAwardingStrategy = () => {
  const { modelConfig, updateModelConfig } = useContext(FaoBuilderContext);
  const { currentClientData } = useContext(ClientContext);

  const [isLoading, setIsLoading] = useState(true);
  const [componentStatus, setComponentStatus] = useState({
    isSaved: false,
    isSaving: false,
  });
  const [funds, setFunds] = useState([]);
  const [outsideAidChecked, setOutsideAidChecked] = useState(
    DEFAULT_OUTSIDE_AID_STATE
  );
  const [groupedFunds, setGroupedFunds] = useState([]);

  const uniqueId = uniqid();

  // Update grouped funds when funds or outside aid changes
  useEffect(() => {
    setComponentStatus((prev) => ({
      ...prev,
      isSaved: false,
    }));
    setGroupedFunds(groupFundsByName(funds));
  }, [funds, outsideAidChecked]);

  // Fetch institutional funds from API
  const fetchInstitutionalFunds = async () => {
    try {
      if (!modelConfig?.id) return;

      const response = await apiRoot.get(
        "/api/fao-awardings/institutional-funds",
        {
          params: {
            modelId: modelConfig.id,
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        const transformedData = transformInstitutionalFundsData(response.data);
        setFunds(transformedData);
      } else {
        setFunds([]);
      }
    } catch (error) {
      console.error("Error fetching institutional funds:", error);
      toast.error("Failed to load institutional funds");
      setFunds([]);
    }
  };

  // Load existing awarding strategy or fetch default data
  const loadAwardingStrategy = async () => {
    setIsLoading(true);

    if (!modelConfig?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const existingStrategyId = modelConfig.configs["awardingStrategy"];

      if (existingStrategyId) {
        const response = await apiRoot.get(
          `/api/fao-awardings/${existingStrategyId}`
        );

        if (response.data) {
          // Load funds from specialFundAdjustments
          if (
            response.data.specialFundAdjustments &&
            Array.isArray(response.data.specialFundAdjustments) &&
            response.data.specialFundAdjustments.length > 0
          ) {
            const transformedFunds = transformSpecialFundAdjustments(
              response.data.specialFundAdjustments
            );
            setFunds(transformedFunds);
          } else {
            await fetchInstitutionalFunds();
          }

          // Load outside aid settings
          const outsideAidSettings = transformOutsideAidData(response.data);
          setOutsideAidChecked(outsideAidSettings);
        } else {
          await fetchInstitutionalFunds();
        }
      } else {
        await fetchInstitutionalFunds();
        setOutsideAidChecked(DEFAULT_OUTSIDE_AID_STATE);
      }
    } catch (error) {
      console.error("Error loading awarding strategy:", error);
      toast.error("Failed to load awarding strategy configuration");
      await fetchInstitutionalFunds();
    }

    setIsLoading(false);
  };

  // Load data when modelConfig changes
  useEffect(() => {
    loadAwardingStrategy();
  }, [modelConfig?.id, modelConfig?.configs]);

  // Handle input changes for funds
  const handleInputChange = ({ fundName, valueIndex, field, value }) => {
    const updatedFunds = [...funds];
    const fundIndices = funds.reduce((acc, fund, index) => {
      if (fund.name === fundName) acc.push(index);
      return acc;
    }, []);

    if (field === "year2026") {
      updatedFunds[fundIndices[valueIndex]][field] = parseCurrency(value);
    } else {
      // Update type for all instances of the fund
      fundIndices.forEach((index) => {
        updatedFunds[index][field] = value;
      });
    }
    setFunds(updatedFunds);
  };

  // Handle outside aid checkbox changes
  const handleOutsideAidCheckChange = (index) => {
    setOutsideAidChecked((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Save awarding strategy
  const saveAwardingStrategy = async () => {
    setComponentStatus((prev) => ({
      ...prev,
      isSaving: true,
    }));

    try {
      const toastId = toast.loading("Saving Awarding Strategy...");

      const existingStrategyId = modelConfig.configs["awardingStrategy"];
      const specialFundAdjustmentsPayload =
        createSpecialFundAdjustmentsPayload(groupedFunds);

      if (existingStrategyId) {
        // Update existing strategy
        const strategyData = {
          id: existingStrategyId,
          schoolKey: currentClientData.schoolKey,
          specialFundAdjustments: specialFundAdjustmentsPayload,
          federalGrantAmount: outsideAidChecked[0] || false,
          pellGrantAmount: outsideAidChecked[1] || false,
          stateGrantAmount: outsideAidChecked[2] || false,
          subsidizedLoanAmount: outsideAidChecked[3] || false,
          unsubsidizedLoanAmount: outsideAidChecked[4] || false,
        };

        await apiRoot.patch(
          `/api/fao-awardings/${existingStrategyId}`,
          strategyData
        );
      } else {
        // Create new strategy
        const strategyData = {
          id: uniqueId,
          schoolKey: currentClientData.schoolKey,
          specialFundAdjustments: specialFundAdjustmentsPayload,
          federalGrantAmount: outsideAidChecked[0] || false,
          pellGrantAmount: outsideAidChecked[1] || false,
          stateGrantAmount: outsideAidChecked[2] || false,
          subsidizedLoanAmount: outsideAidChecked[3] || false,
          unsubsidizedLoanAmount: outsideAidChecked[4] || false,
        };

        await apiRoot.post("/api/fao-awardings", strategyData);
        await updateModelConfig("awardingStrategy", uniqueId);
      }

      toast.update(toastId, {
        render: "SUCCESS - Awarding Strategy Saved",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving awarding strategy:", error);
      toast.error(error.message || "Failed to save awarding strategy");
    }

    setComponentStatus((prev) => ({
      ...prev,
      isSaving: false,
      isSaved: true,
    }));
  };

  return {
    // State
    isLoading,
    funds,
    outsideAidChecked,
    groupedFunds,
    componentStatus,
    modelConfig,

    // Actions
    handleInputChange,
    handleOutsideAidCheckChange,
    saveAwardingStrategy,
  };
};
